import React from "react";
import { FaLock } from "react-icons/fa";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiArrowRight,
  FiBox,
  FiGlobe,
  FiCalendar,
  FiCheck,
  FiDownload,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiClock,
  FiCopy,
  FiExternalLink,
  FiFileText,
  FiMapPin,
  FiMessageCircle,
  FiMinus,
  FiPlus,
  FiPlusCircle,
  FiSearch,
  FiUser,
  FiX,
  FiLogOut,
  FiInfo,
  FiChevronDown,
  FiInstagram,
} from "react-icons/fi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { IoMdPricetag, IoIosInformationCircle } from "react-icons/io";
import { GoXCircle } from "react-icons/go";
import { cssVariables } from "./css_variables";

export type IconName =
  | "x"
  | "chevron-up"
  | "plus"
  | "lock"
  | "lock-alt"
  | "check"
  | "star"
  | "download"
  | "globe"
  | "user"
  | "arrow-left"
  | "arrow-right"
  | "minus"
  | "alert-triangle"
  | "check-circle"
  | "clock"
  | "external-link"
  | "chevron-left"
  | "chevrons-left"
  | "chevron-right"
  | "chevrons-right"
  | "plus-circle"
  | "copy"
  | "message-circle"
  | "box"
  | "share"
  | "layout"
  | "chevron-down"
  | "fi-chevron-down"
  | "map-pin"
  | "calendar"
  | "city"
  | "location"
  | "capacity"
  | "filter"
  | "location_pin"
  | "search"
  | "logout"
  | "info"
  | "filled-info"
  | "pencil"
  | "dot"
  | "state"
  | "file-text"
  | "people"
  | "bill"
  | "check-full-circle"
  | "go-x-circle"
  | "price-tag"
  | "eye-outline"
  | "eye-off-outline"
  | "instagram"
  | "product-category";

export interface IconProps {
  /**
   * Name if  the icon.
   */
  name: IconName;
  color?: string;
  className?: string;
  size?: number;
}

export function Icon(props: IconProps) {
  const {
    name,
    color = cssVariables.textColorDefault,
    className,
    size = 24,
  } = props;

  switch (name) {
    case "pencil":
      return <Pencil color={color} className={className} size={size} />;
    case "dot":
      return <Dot {...props} />;
    case "state":
      return <State {...props} />;
    case "share":
      return <ShareIcon color={color} className={className} size={size} />;
    case "layout":
      return <LayoutIcon color={color} className={className} size={size} />;
    case "calendar":
      return (
        <FiCalendar
          strokeWidth={1.2}
          color={color}
          className={className}
          size={size}
        />
      );
    case "capacity":
      return <IconCapacity className={className} />;
    case "city":
      return <IconCity />;
    case "location":
      return <IconLocation />;
    case "filter":
      return <IconFilter />;
    case "star":
      return <StarIcon />;
    case "chevron-down":
      return <ChevronDownIcon className={className} />;
    case "chevron-up":
      return <ChevronUpIcon className={className} />;
    case "location_pin":
      return (
        <LocationPinIcon color={color} className={className} size={size} />
      );
    case "arrow-left":
      return <FiArrowLeft color={color} className={className} size={size} />;
    case "globe":
      return <FiGlobe color={color} className={className} size={size} />;
    case "arrow-right":
      return <FiArrowRight color={color} className={className} size={size} />;
    case "check":
      return <FiCheck color={color} className={className} size={size} />;
    case "x":
      return <FiX color={color} className={className} size={size} />;
    case "minus":
      return <FiMinus color={color} className={className} size={size} />;
    case "map-pin":
      return (
        <FiMapPin
          strokeWidth={1.5}
          color={color}
          className={className}
          size={size}
        />
      );
    case "message-circle":
      return (
        <FiMessageCircle color={color} className={className} size={size} />
      );
    case "plus":
      return <FiPlus color={color} className={className} size={size} />;
    case "alert-triangle":
      return (
        <FiAlertTriangle color={color} className={className} size={size} />
      );
    case "check-circle":
      return <FiCheckCircle color={color} className={className} size={size} />;
    case "clock":
      return <FiClock color={color} className={className} size={size} />;
    case "download":
      return (
        <FiDownload
          strokeWidth={1}
          color={color}
          className={className}
          size={size}
        />
      );
    case "external-link":
      return <FiExternalLink color={color} className={className} size={size} />;
    case "chevron-left":
      return <FiChevronLeft color={color} className={className} size={size} />;
    case "fi-chevron-down":
      return <FiChevronDown color={color} className={className} size={size} />;
    case "chevrons-left":
      return <FiChevronsLeft color={color} className={className} size={size} />;
    case "chevron-right":
      return <FiChevronRight color={color} className={className} size={size} />;
    case "chevrons-right":
      return (
        <FiChevronsRight color={color} className={className} size={size} />
      );
    case "plus-circle":
      return <FiPlusCircle color={color} className={className} size={size} />;
    case "lock":
      return <FaLock color={color} className={className} size={size} />;
    case "lock-alt":
      return <IconLockAlt />;
    case "copy":
      return <FiCopy color={color} className={className} size={size} />;
    case "box":
      return (
        <FiBox
          strokeWidth={1.5}
          color={color}
          className={className}
          size={size}
        />
      );
    case "search":
      return <FiSearch color={color} className={className} size={size} />;
    case "user":
      return <FiUser color={color} className={className} size={size} />;
    case "file-text":
      return <FiFileText color={color} className={className} size={size} />;
    case "logout":
      return <FiLogOut color={color} className={className} size={size} />;
    case "info":
      return <FiInfo color={color} className={className} size={size} />;
    case "filled-info":
      return (
        <IoIosInformationCircle
          color={color}
          className={className}
          size={size}
        />
      );
    case "price-tag":
      return <IoMdPricetag color={color} className={className} size={size} />;
    case "people":
      return <IconPeople className={className} />;
    case "bill":
      return <IconBill />;
    case "check-full-circle":
      return (
        <CheckFullCircle color={color} className={className} size={size} />
      );
    case "go-x-circle":
      return <GoXCircle color={color} className={className} size={size} />;
    case "eye-outline":
      return <IoEyeOutline color={color} className={className} size={size} />;
    case "eye-off-outline":
      return (
        <IoEyeOffOutline color={color} className={className} size={size} />
      );
    case "instagram":
      return <FiInstagram color={color} className={className} size={size} />;
    case "product-category":
      return (
        <HiOutlineOfficeBuilding
          color={color}
          className={className}
          size={size}
        />
      );
    default:
      throw new Error("Missing icon");
  }
}

function IconCapacity(props: Partial<IconProps>) {
  const { className } = props;
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.3333 19V17.3333C16.3333 16.4493 15.9821 15.6014 15.357 14.9763C14.7319 14.3512 13.8841 14 13 14H6.33333C5.44928 14 4.60143 14.3512 3.97631 14.9763C3.35119 15.6014 3 16.4493 3 17.3333V19"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.66671 10.6667C11.5077 10.6667 13 9.17428 13 7.33333C13 5.49238 11.5077 4 9.66671 4C7.82576 4 6.33337 5.49238 6.33337 7.33333C6.33337 9.17428 7.82576 10.6667 9.66671 10.6667Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21.3334 19.0001V17.3334C21.3328 16.5948 21.087 15.8774 20.6345 15.2937C20.182 14.7099 19.5485 14.293 18.8334 14.1084"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.5 4.1084C16.217 4.29198 16.8525 4.70898 17.3064 5.29366C17.7602 5.87833 18.0065 6.59742 18.0065 7.33757C18.0065 8.07771 17.7602 8.7968 17.3064 9.38147C16.8525 9.96615 16.217 10.3831 15.5 10.5667"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCity() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.2845 3.00417C12.2845 3.61462 12.2845 4.22216 12.2845 4.83261C12.2845 6.29478 12.2845 7.75694 12.2845 9.21911C12.2845 10.9778 12.2845 12.7365 12.2845 14.4951C12.2845 16.0212 12.2845 17.5503 12.2845 19.0764C12.2845 19.5764 12.2845 20.0764 12.2845 20.5764C12.2845 20.8147 12.2729 21.0531 12.2845 21.2914C12.2845 21.3031 12.2845 21.3118 12.2845 21.3234C12.4299 21.1781 12.5752 21.0327 12.7206 20.8874C12.3107 20.8874 11.9037 20.8874 11.4939 20.8874C10.5171 20.8874 9.54043 20.8874 8.56662 20.8874C7.38642 20.8874 6.20622 20.8874 5.02602 20.8874C3.99989 20.8874 2.97666 20.8874 1.95053 20.8874C1.45635 20.8874 0.953462 20.8612 0.45929 20.8874C0.453476 20.8874 0.444755 20.8874 0.438941 20.8874C0.584286 21.0327 0.729631 21.1781 0.874976 21.3234C0.874976 20.9019 0.874976 20.4775 0.874976 20.056C0.874976 18.9921 0.874976 17.9282 0.874976 16.8642C0.874976 15.4631 0.874976 14.0591 0.874976 12.658C0.874976 11.222 0.874976 9.78596 0.874976 8.34995C0.874976 7.18429 0.874976 6.01862 0.874976 4.85005C0.874976 4.25704 0.874976 3.66694 0.874976 3.07394C0.874976 3.05359 0.874976 3.03615 0.874976 3.0158C0.874976 3.01289 0.874976 3.00708 0.874976 3.00417C0.874976 3.00417 0.874976 3.00417 0.874976 3.00126C0.874976 2.99545 0.877883 2.93441 0.874976 2.98092C0.877883 2.94894 0.874976 2.95475 0.872069 2.99836C0.874976 2.97801 0.880789 2.96057 0.886603 2.94313C0.88951 2.93731 0.88951 2.9315 0.892417 2.92568C0.872069 2.96929 0.874975 2.97219 0.89823 2.92568C0.947648 2.91696 0.883696 2.85592 0.89823 2.92278C0.895324 2.91115 0.947648 2.87627 0.895324 2.91696C0.895324 2.91696 0.999972 2.85883 0.941834 2.88208C0.886603 2.90824 0.999972 2.86755 0.999972 2.86755C0.924393 2.88499 0.979624 2.87045 1.00288 2.87045H1.00579C1.02323 2.87045 1.04358 2.87045 1.06102 2.87045C1.34008 2.87045 1.62205 2.87045 1.90111 2.87045C3.11328 2.87045 4.32546 2.87045 5.53473 2.87045C6.94457 2.87045 8.35151 2.87045 9.76135 2.87045C10.5433 2.87045 11.3282 2.86464 12.1101 2.87045C12.1218 2.87045 12.1363 2.87045 12.1479 2.87045C12.1537 2.87045 12.1915 2.87336 12.1683 2.87045C12.1741 2.87045 12.1828 2.87045 12.1886 2.87045C12.1392 2.86173 12.1392 2.86464 12.1886 2.87627C12.1857 2.87336 12.2787 2.92568 12.1915 2.87627C12.1915 2.87627 12.2962 2.92859 12.2409 2.90534C12.1915 2.88499 12.2904 2.96929 12.2322 2.89371C12.2467 2.91406 12.2584 2.93731 12.2729 2.95766C12.2235 2.87045 12.2729 2.96347 12.2729 2.96057C12.2758 2.97219 12.2787 2.98673 12.2816 2.99836C12.2816 2.95185 12.2816 2.95185 12.2845 3.00417C12.2933 3.23091 12.4793 3.45183 12.7206 3.44021C12.9502 3.42858 13.1653 3.24835 13.1566 3.00417C13.1363 2.44023 12.706 2.01583 12.1421 2.00129C12.0723 1.99839 12.0026 2.00129 11.9328 2.00129C11.5549 2.00129 11.1741 2.00129 10.7962 2.00129C9.46776 2.00129 8.13931 2.00129 6.81086 2.00129C5.44171 2.00129 4.07256 2.00129 2.70341 2.00129C2.26738 2.00129 1.83425 2.00129 1.39822 2.00129C1.05811 2.00129 0.718003 1.97222 0.424407 2.19024C0.148252 2.39372 0.0029069 2.69604 0 3.03615C0 3.09719 0 3.15824 0 3.21928C0 3.49544 0 3.77159 0 4.04775C0 5.0826 0 6.11746 0 7.15231C0 8.56215 0 9.972 0 11.3818C0 12.844 0 14.3033 0 15.7654C0 16.966 0 18.1665 0 19.3671C0 19.9921 0 20.6171 0 21.242C0 21.2711 0 21.2973 0 21.3263C0 21.5618 0.200576 21.7624 0.436034 21.7624C0.845907 21.7624 1.25287 21.7624 1.66274 21.7624C2.63946 21.7624 3.61618 21.7624 4.58999 21.7624C5.77019 21.7624 6.95039 21.7624 8.13059 21.7624C9.15672 21.7624 10.1799 21.7624 11.2061 21.7624C11.7032 21.7624 12.2002 21.7769 12.6973 21.7624C12.7031 21.7624 12.7119 21.7624 12.7177 21.7624C12.9531 21.7624 13.1537 21.5618 13.1537 21.3263C13.1537 20.7159 13.1537 20.1083 13.1537 19.4979C13.1537 18.0357 13.1537 16.5736 13.1537 15.1114C13.1537 13.3527 13.1537 11.594 13.1537 9.83537C13.1537 8.30925 13.1537 6.78023 13.1537 5.25411C13.1537 4.51576 13.1711 3.7774 13.1537 3.03905C13.1537 3.02743 13.1537 3.01871 13.1537 3.00708C13.1537 2.78034 12.9531 2.55942 12.7177 2.57104C12.4851 2.57976 12.2845 2.75999 12.2845 3.00417Z"
        fill="black"
      />
      <path
        d="M8.93576 14.7509C7.55789 14.7509 6.18293 14.7509 4.80506 14.7509C4.6074 14.7509 4.40973 14.7509 4.21496 14.7509C3.97951 14.7509 3.77893 14.9515 3.77893 15.187C3.77893 15.8817 3.77893 16.5765 3.77893 17.2712C3.77893 18.3671 3.77893 19.463 3.77893 20.5618C3.77893 20.8176 3.77893 21.0734 3.77893 21.3263C3.77893 21.5618 3.97951 21.7624 4.21496 21.7624C5.59283 21.7624 6.9678 21.7624 8.34566 21.7624C8.54333 21.7624 8.741 21.7624 8.93576 21.7624C9.17122 21.7624 9.3718 21.5618 9.3718 21.3263C9.3718 20.6316 9.3718 19.9368 9.3718 19.2421C9.3718 18.1462 9.3718 17.0503 9.3718 15.9515C9.3718 15.6957 9.3718 15.4399 9.3718 15.187C9.3718 14.9602 9.17122 14.7393 8.93576 14.7509C8.7003 14.7626 8.49973 14.9428 8.49973 15.187C8.49973 15.8817 8.49973 16.5765 8.49973 17.2712C8.49973 18.3671 8.49973 19.463 8.49973 20.5618C8.49973 20.8176 8.49973 21.0734 8.49973 21.3263C8.64507 21.181 8.79042 21.0356 8.93576 20.8903C7.55789 20.8903 6.18293 20.8903 4.80506 20.8903C4.6074 20.8903 4.40973 20.8903 4.21496 20.8903C4.36031 21.0356 4.50565 21.181 4.651 21.3263C4.651 20.6316 4.651 19.9368 4.651 19.2421C4.651 18.1462 4.651 17.0503 4.651 15.9515C4.651 15.6957 4.651 15.4399 4.651 15.187C4.50565 15.3323 4.36031 15.4777 4.21496 15.623C5.59283 15.623 6.9678 15.623 8.34566 15.623C8.54333 15.623 8.741 15.623 8.93576 15.623C9.1625 15.623 9.38342 15.4224 9.3718 15.187C9.36308 14.9515 9.17994 14.7509 8.93576 14.7509Z"
        fill="black"
      />
      <path
        d="M9.37182 7.52145C9.37182 6.88484 9.37182 6.24532 9.37182 5.60872C9.37182 5.38198 9.17125 5.16105 8.93579 5.17268C8.70033 5.18431 8.49976 5.36454 8.49976 5.60872C8.49976 6.24532 8.49976 6.88484 8.49976 7.52145C8.49976 7.74819 8.70033 7.96911 8.93579 7.95749C9.17416 7.94876 9.37182 7.76854 9.37182 7.52145Z"
        fill="black"
      />
      <path
        d="M3.77893 5.60872C3.77893 6.24532 3.77893 6.88484 3.77893 7.52145C3.77893 7.74819 3.97951 7.96911 4.21496 7.95749C4.45042 7.94586 4.651 7.76563 4.651 7.52145C4.651 6.88484 4.651 6.24532 4.651 5.60872C4.651 5.38198 4.45042 5.16105 4.21496 5.17268C3.97951 5.18431 3.77893 5.36454 3.77893 5.60872Z"
        fill="black"
      />
      <path
        d="M9.37182 12.3469C9.37182 11.7103 9.37182 11.0708 9.37182 10.4342C9.37182 10.2074 9.17125 9.98649 8.93579 9.99812C8.70033 10.0097 8.49976 10.19 8.49976 10.4342C8.49976 11.0708 8.49976 11.7103 8.49976 12.3469C8.49976 12.5736 8.70033 12.7945 8.93579 12.7829C9.17416 12.7742 9.37182 12.5911 9.37182 12.3469Z"
        fill="black"
      />
      <path
        d="M3.77893 10.4342C3.77893 11.0708 3.77893 11.7103 3.77893 12.3469C3.77893 12.5736 3.97951 12.7945 4.21496 12.7829C4.45042 12.7713 4.651 12.5911 4.651 12.3469C4.651 11.7103 4.651 11.0708 4.651 10.4342C4.651 10.2074 4.45042 9.98649 4.21496 9.99812C3.97951 10.0097 3.77893 10.19 3.77893 10.4342Z"
        fill="black"
      />
      <path
        d="M22.1215 9.99531C22.1215 10.3703 22.1215 10.7453 22.1215 11.1174C22.1215 12.0214 22.1215 12.9255 22.1215 13.8295C22.1215 14.9196 22.1215 16.0097 22.1215 17.0969C22.1215 18.0387 22.1215 18.9805 22.1215 19.9253C22.1215 20.3816 22.0953 20.8497 22.1215 21.306C22.1215 21.3119 22.1215 21.3177 22.1215 21.3264C22.2668 21.181 22.4122 21.0357 22.5575 20.8904C21.4413 20.8904 20.3279 20.8904 19.2117 20.8904C17.4501 20.8904 15.6856 20.8904 13.924 20.8904C13.5229 20.8904 13.1217 20.8904 12.7206 20.8904C12.8659 21.0357 13.0113 21.181 13.1566 21.3264C13.1566 20.9311 13.1566 20.5386 13.1566 20.1433C13.1566 19.1956 13.1566 18.2509 13.1566 17.3032C13.1566 16.1579 13.1566 15.0126 13.1566 13.8673C13.1566 12.876 13.1566 11.8848 13.1566 10.8935C13.1566 10.4139 13.1857 9.92555 13.1566 9.44591C13.1566 9.44009 13.1566 9.43137 13.1566 9.42556C13.0113 9.5709 12.8659 9.71625 12.7206 9.86159C13.831 9.86159 14.9414 9.86159 16.049 9.86159C17.7321 9.86159 19.4122 9.86159 21.0953 9.86159C21.3395 9.86159 21.5808 9.86159 21.825 9.86159C21.8744 9.86159 21.9267 9.86159 21.9761 9.86159C21.979 9.86159 21.9848 9.86159 21.9877 9.86159C21.9877 9.86159 21.9877 9.86159 21.9907 9.86159C22.0052 9.86159 22.0168 9.86159 22.0314 9.86159C21.9819 9.85287 21.9819 9.85578 22.0314 9.86741C22.043 9.87031 22.0546 9.87613 22.0691 9.87903C22.0255 9.85869 22.0226 9.86159 22.0691 9.88485C22.1098 9.91392 22.1127 9.91392 22.072 9.88485C22.104 9.92264 22.1069 9.91973 22.0779 9.88194C22.0866 9.89648 22.1389 10.0011 22.104 9.91392C22.1127 9.93717 22.1215 9.96333 22.1244 9.9895C22.1186 9.94008 22.1186 9.94008 22.1215 9.99531C22.1302 10.222 22.3162 10.443 22.5575 10.4313C22.7871 10.4197 23.0023 10.2395 22.9935 9.99531C22.9732 9.40812 22.5168 8.99534 21.9383 8.99243C21.7726 8.99243 21.6069 8.99243 21.4383 8.99243C20.732 8.99243 20.0227 8.99243 19.3163 8.99243C17.5373 8.99243 15.7612 8.99243 13.9822 8.99243C13.5607 8.99243 13.1392 8.99243 12.7206 8.99243C12.4851 8.99243 12.2845 9.19301 12.2845 9.42847C12.2845 9.8238 12.2845 10.2162 12.2845 10.6116C12.2845 11.5592 12.2845 12.504 12.2845 13.4516C12.2845 14.5969 12.2845 15.7422 12.2845 16.8876C12.2845 17.8788 12.2845 18.8701 12.2845 19.8613C12.2845 20.3439 12.27 20.8264 12.2845 21.3089C12.2845 21.3148 12.2845 21.3235 12.2845 21.3293C12.2845 21.5648 12.4851 21.7653 12.7206 21.7653C13.8368 21.7653 14.9502 21.7653 16.0664 21.7653C17.828 21.7653 19.5925 21.7653 21.354 21.7653C21.7552 21.7653 22.1563 21.7653 22.5575 21.7653C22.793 21.7653 22.9935 21.5648 22.9935 21.3293C22.9935 20.9543 22.9935 20.5793 22.9935 20.2072C22.9935 19.3032 22.9935 18.3991 22.9935 17.4951C22.9935 16.405 22.9935 15.3149 22.9935 14.2278C22.9935 13.2859 22.9935 12.3441 22.9935 11.3993C22.9935 10.9401 23.0081 10.4779 22.9935 10.0186C22.9935 10.0128 22.9935 10.0069 22.9935 9.99822C22.9935 9.77148 22.793 9.55056 22.5575 9.56218C22.3191 9.568 22.1215 9.74822 22.1215 9.99531Z"
        fill="black"
      />
      <path
        d="M19.8571 13.9195C19.8571 13.2829 19.8571 12.6434 19.8571 12.0068C19.8571 11.78 19.6565 11.5591 19.421 11.5708C19.1856 11.5824 18.985 11.7626 18.985 12.0068C18.985 12.6434 18.985 13.2829 18.985 13.9195C18.985 14.1463 19.1856 14.3672 19.421 14.3556C19.6565 14.3468 19.8571 14.1637 19.8571 13.9195Z"
        fill="black"
      />
      <path
        d="M15.4269 12.0068C15.4269 12.6434 15.4269 13.2829 15.4269 13.9195C15.4269 14.1463 15.6275 14.3672 15.8629 14.3556C16.0984 14.3439 16.2989 14.1637 16.2989 13.9195C16.2989 13.2829 16.2989 12.6434 16.2989 12.0068C16.2989 11.78 16.0984 11.5591 15.8629 11.5708C15.6245 11.5824 15.4269 11.7626 15.4269 12.0068Z"
        fill="black"
      />
      <path
        d="M19.8571 18.745C19.8571 18.1084 19.8571 17.4688 19.8571 16.8322C19.8571 16.6055 19.6565 16.3846 19.421 16.3962C19.1856 16.4078 18.985 16.588 18.985 16.8322C18.985 17.4688 18.985 18.1084 18.985 18.745C18.985 18.9717 19.1856 19.1926 19.421 19.181C19.6565 19.1694 19.8571 18.9891 19.8571 18.745Z"
        fill="black"
      />
      <path
        d="M15.4269 16.8322C15.4269 17.4688 15.4269 18.1084 15.4269 18.745C15.4269 18.9717 15.6275 19.1926 15.8629 19.181C16.0984 19.1694 16.2989 18.9891 16.2989 18.745C16.2989 18.1084 16.2989 17.4688 16.2989 16.8322C16.2989 16.6055 16.0984 16.3846 15.8629 16.3962C15.6245 16.4049 15.4269 16.588 15.4269 16.8322Z"
        fill="black"
      />
    </svg>
  );
}

function IconLocation() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.5455 9.77273C19.5455 15.8182 11.7727 21 11.7727 21C11.7727 21 4 15.8182 4 9.77273C4 7.71127 4.81891 5.73425 6.27658 4.27658C7.73425 2.81891 9.71127 2 11.7727 2C13.8342 2 15.8112 2.81891 17.2689 4.27658C18.7265 5.73425 19.5455 7.71127 19.5455 9.77273Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.7727 12.3637C13.2036 12.3637 14.3636 11.2037 14.3636 9.77279C14.3636 8.34187 13.2036 7.18188 11.7727 7.18188C10.3418 7.18188 9.18176 8.34187 9.18176 9.77279C9.18176 11.2037 10.3418 12.3637 11.7727 12.3637Z"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.296 6.36022H4.43997C4.10397 6.36022 3.83997 6.62422 3.83997 6.96022C3.83997 7.29622 4.10397 7.56022 4.43997 7.56022H13.296C13.56 8.44822 14.376 9.09622 15.36 9.09622C16.32 9.09622 17.16 8.44822 17.4 7.56022H19.584C19.92 7.56022 20.184 7.29622 20.184 6.96022C20.184 6.62422 19.92 6.36022 19.584 6.36022H17.4C17.136 5.47222 16.32 4.82422 15.36 4.82422C14.376 4.82422 13.56 5.47222 13.296 6.36022ZM16.272 6.96022C16.272 7.48822 15.84 7.89622 15.336 7.89622C14.832 7.89622 14.4 7.48822 14.4 6.96022C14.4 6.43222 14.832 6.02422 15.336 6.02422C15.816 6.02422 16.272 6.43222 16.272 6.96022Z"
        fill="black"
      />
      <path
        d="M6.09597 11.16H4.43997C4.10397 11.16 3.83997 11.424 3.83997 11.76C3.83997 12.096 4.10397 12.36 4.43997 12.36H6.09597C6.35997 13.248 7.17597 13.896 8.15997 13.896C9.11997 13.896 9.95997 13.248 10.2 12.36H19.584C19.92 12.36 20.184 12.096 20.184 11.76C20.184 11.424 19.92 11.16 19.584 11.16H10.2C9.93597 10.272 9.11997 9.62402 8.15997 9.62402C7.17597 9.62402 6.35997 10.272 6.09597 11.16ZM9.07197 11.76C9.07197 12.288 8.63997 12.696 8.13597 12.696C7.63197 12.696 7.19997 12.288 7.19997 11.76C7.19997 11.232 7.63197 10.824 8.13597 10.824C8.63997 10.824 9.07197 11.232 9.07197 11.76Z"
        fill="black"
      />
      <path
        d="M10.416 16.4398H4.43997C4.10397 16.4398 3.83997 16.7038 3.83997 17.0398C3.83997 17.3758 4.10397 17.6398 4.43997 17.6398H10.416C10.68 18.5278 11.496 19.1758 12.48 19.1758C13.44 19.1758 14.28 18.5278 14.52 17.6398H19.584C19.92 17.6398 20.184 17.3758 20.184 17.0398C20.184 16.7038 19.92 16.4398 19.584 16.4398H14.52C14.256 15.5518 13.44 14.9038 12.48 14.9038C11.496 14.9038 10.68 15.5518 10.416 16.4398ZM13.392 17.0398C13.392 17.5678 12.96 17.9758 12.456 17.9758C11.952 17.9758 11.52 17.5678 11.52 17.0398C11.52 16.5118 11.952 16.1038 12.456 16.1038C12.96 16.1038 13.392 16.5118 13.392 17.0398Z"
        fill="black"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg
      width="16"
      height="15"
      viewBox="0 0 16 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.00001 1.33337L10.06 5.50671L14.6667 6.18004L11.3333 9.42671L12.12 14.0134L8.00001 11.8467L3.88001 14.0134L4.66668 9.42671L1.33334 6.18004L5.94001 5.50671L8.00001 1.33337Z"
        fill="#FFCC02"
        stroke="#FFCC02"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPeople(props: Partial<IconProps>) {
  const { className } = props;
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M7.68001 4.98002C8.05975 4.98002 8.43096 4.86741 8.7467 4.65644C9.06245 4.44546 9.30854 4.1456 9.45386 3.79477C9.59918 3.44393 9.6372 3.05789 9.56312 2.68544C9.48903 2.313 9.30617 1.97089 9.03765 1.70237C8.76914 1.43385 8.42703 1.25099 8.05458 1.17691C7.68214 1.10282 7.29609 1.14085 6.94526 1.28617C6.59442 1.43149 6.29456 1.67758 6.08359 1.99332C5.87262 2.30906 5.76001 2.68028 5.76001 3.06002C5.76001 3.56923 5.96229 4.05759 6.32236 4.41766C6.68243 4.77773 7.17079 4.98002 7.68001 4.98002ZM7.68001 1.78002C7.93317 1.78002 8.18064 1.85509 8.39114 1.99573C8.60163 2.13638 8.7657 2.33629 8.86258 2.57018C8.95946 2.80407 8.9848 3.06144 8.93542 3.30973C8.88603 3.55803 8.76412 3.7861 8.58511 3.96511C8.4061 4.14412 8.17802 4.26603 7.92973 4.31542C7.68143 4.36481 7.42406 4.33946 7.19018 4.24258C6.95629 4.1457 6.75638 3.98164 6.61573 3.77115C6.47508 3.56065 6.40001 3.31318 6.40001 3.06002C6.40001 2.72054 6.53487 2.39497 6.77491 2.15492C7.01496 1.91487 7.34053 1.78002 7.68001 1.78002Z"
        fill="black"
      />
      <path
        d="M12.16 4.34C12.5397 4.34 12.9109 4.22739 13.2267 4.01642C13.5424 3.80545 13.7885 3.50559 13.9338 3.15475C14.0792 2.80392 14.1172 2.41787 14.0431 2.04543C13.969 1.67298 13.7862 1.33087 13.5176 1.06236C13.2491 0.793839 12.907 0.610977 12.5346 0.536893C12.1621 0.46281 11.7761 0.500832 11.4252 0.646152C11.0744 0.791472 10.7745 1.03756 10.5636 1.35331C10.3526 1.66905 10.24 2.04026 10.24 2.42C10.24 2.92922 10.4423 3.41758 10.8023 3.77765C11.1624 4.13772 11.6508 4.34 12.16 4.34ZM12.16 1.14C12.4132 1.14 12.6606 1.21507 12.8711 1.35572C13.0816 1.49637 13.2457 1.69628 13.3426 1.93017C13.4394 2.16406 13.4648 2.42142 13.4154 2.66972C13.366 2.91801 13.2441 3.14609 13.0651 3.3251C12.8861 3.50411 12.658 3.62602 12.4097 3.67541C12.1614 3.7248 11.904 3.69945 11.6702 3.60257C11.4363 3.50569 11.2364 3.34163 11.0957 3.13113C10.9551 2.92064 10.88 2.67316 10.88 2.42C10.88 2.08052 11.0148 1.75495 11.2549 1.5149C11.4949 1.27486 11.8205 1.14 12.16 1.14Z"
        fill="black"
      />
      <path
        d="M3.20003 4.34C3.57977 4.34 3.95098 4.22739 4.26672 4.01642C4.58247 3.80545 4.82856 3.50559 4.97388 3.15475C5.1192 2.80392 5.15722 2.41787 5.08314 2.04543C5.00905 1.67298 4.82619 1.33087 4.55767 1.06236C4.28916 0.793839 3.94705 0.610977 3.5746 0.536893C3.20216 0.46281 2.81611 0.500832 2.46528 0.646152C2.11444 0.791472 1.81458 1.03756 1.60361 1.35331C1.39264 1.66905 1.28003 2.04026 1.28003 2.42C1.28003 2.92922 1.48231 3.41758 1.84238 3.77765C2.20245 4.13772 2.69081 4.34 3.20003 4.34ZM3.20003 1.14C3.45319 1.14 3.70066 1.21507 3.91116 1.35572C4.12165 1.49637 4.28571 1.69628 4.3826 1.93017C4.47948 2.16406 4.50482 2.42142 4.45543 2.66972C4.40605 2.91801 4.28414 3.14609 4.10513 3.3251C3.92611 3.50411 3.69804 3.62602 3.44974 3.67541C3.20145 3.7248 2.94408 3.69945 2.71019 3.60257C2.4763 3.50569 2.2764 3.34163 2.13575 3.13113C1.9951 2.92064 1.92003 2.67316 1.92003 2.42C1.92003 2.08052 2.05489 1.75495 2.29493 1.5149C2.53498 1.27486 2.86055 1.14 3.20003 1.14Z"
        fill="black"
      />
      <path
        d="M12.16 4.98001C11.6617 4.98064 11.1705 5.09816 10.7259 5.32314C10.2813 5.54811 9.89567 5.87426 9.60002 6.27537C9.04977 5.85125 8.37453 5.62134 7.6798 5.62157C6.98507 5.6218 6.30998 5.85216 5.76002 6.27665C5.36083 5.73489 4.80058 5.33319 4.15937 5.12898C3.51815 4.92478 2.82879 4.92851 2.18983 5.13965C1.55086 5.3508 0.995001 5.75854 0.601705 6.30459C0.20841 6.85065 -0.00218749 7.50707 1.71341e-05 8.18001L1.71341e-05 9.78001C1.71341e-05 10.0346 0.10116 10.2788 0.281195 10.4588C0.46123 10.6389 0.705409 10.74 0.960017 10.74H1.28002L1.28002 13.62C1.28002 13.8746 1.38116 14.1188 1.56119 14.2988C1.74123 14.4789 1.98541 14.58 2.24002 14.58H4.16002C4.41462 14.58 4.6588 14.4789 4.83884 14.2988C5.01887 14.1188 5.12002 13.8746 5.12002 13.62V11.9611C5.2225 11.999 5.33076 12.0189 5.44002 12.02H5.76002V15.54C5.76002 15.7946 5.86116 16.0388 6.04119 16.2188C6.22123 16.3989 6.46541 16.5 6.72002 16.5H8.64002C8.89462 16.5 9.1388 16.3989 9.31884 16.2188C9.49887 16.0388 9.60002 15.7946 9.60002 15.54L9.60002 12.02H9.92002C10.0293 12.0189 10.1375 11.999 10.24 11.9611L10.24 13.62C10.24 13.8746 10.3412 14.1188 10.5212 14.2988C10.7012 14.4789 10.9454 14.58 11.2 14.58H13.12C13.3746 14.58 13.6188 14.4789 13.7988 14.2988C13.9789 14.1188 14.08 13.8746 14.08 13.62L14.08 10.74H14.4C14.6546 10.74 14.8988 10.6389 15.0788 10.4588C15.2589 10.2788 15.36 10.0346 15.36 9.78001V8.18001C15.36 7.75978 15.2772 7.34366 15.1164 6.95542C14.9556 6.56718 14.7199 6.21442 14.4228 5.91727C14.1256 5.62012 13.7728 5.38441 13.3846 5.2236C12.9964 5.06278 12.5802 4.98001 12.16 4.98001ZM4.48002 13.62C4.48002 13.7049 4.4463 13.7863 4.38629 13.8463C4.32628 13.9063 4.24489 13.94 4.16002 13.94H2.24002C2.15515 13.94 2.07375 13.9063 2.01374 13.8463C1.95373 13.7863 1.92002 13.7049 1.92002 13.62L1.92002 10.74C1.92002 10.5703 1.85259 10.4075 1.73257 10.2875C1.61254 10.1674 1.44976 10.1 1.28002 10.1H0.960017C0.875148 10.1 0.793755 10.0663 0.733743 10.0063C0.673731 9.94627 0.640017 9.86488 0.640017 9.78001L0.640017 8.18001C0.638275 7.63563 0.810421 7.10491 1.13137 6.66519C1.45232 6.22548 1.9053 5.89975 2.42431 5.73546C2.94331 5.57117 3.50122 5.57692 4.01673 5.75185C4.53225 5.92679 4.97843 6.26178 5.29026 6.70801C4.76999 7.28863 4.48159 8.0404 4.48002 8.82001L4.48002 13.62ZM9.92002 11.38H9.60002C9.43028 11.38 9.26749 11.4474 9.14747 11.5675C9.02745 11.6875 8.96002 11.8503 8.96002 12.02L8.96002 15.54C8.96002 15.6249 8.9263 15.7063 8.86629 15.7663C8.80628 15.8263 8.72489 15.86 8.64002 15.86L6.72002 15.86C6.63515 15.86 6.55375 15.8263 6.49374 15.7663C6.43373 15.7063 6.40002 15.6249 6.40002 15.54V12.02C6.40002 11.8503 6.33259 11.6875 6.21256 11.5675C6.09254 11.4474 5.92976 11.38 5.76002 11.38H5.44002C5.35515 11.38 5.27375 11.3463 5.21374 11.2863C5.15373 11.2263 5.12002 11.1449 5.12002 11.06L5.12002 8.82001C5.12002 8.14106 5.38973 7.48991 5.86982 7.00982C6.34992 6.52972 7.00106 6.26001 7.68002 6.26001C8.35897 6.26001 9.01012 6.52972 9.49021 7.00982C9.9703 7.48991 10.24 8.14106 10.24 8.82001V11.06C10.24 11.1449 10.2063 11.2263 10.1463 11.2863C10.0863 11.3463 10.0049 11.38 9.92002 11.38ZM14.72 9.78001C14.72 9.86488 14.6863 9.94627 14.6263 10.0063C14.5663 10.0663 14.4849 10.1 14.4 10.1H14.08C13.9103 10.1 13.7475 10.1674 13.6275 10.2875C13.5074 10.4075 13.44 10.5703 13.44 10.74L13.44 13.62C13.44 13.7049 13.4063 13.7863 13.3463 13.8463C13.2863 13.9063 13.2049 13.94 13.12 13.94H11.2C11.1151 13.94 11.0338 13.9063 10.9737 13.8463C10.9137 13.7863 10.88 13.7049 10.88 13.62L10.88 8.82001C10.8784 8.0404 10.59 7.28863 10.0698 6.70801C10.3816 6.26178 10.8278 5.92679 11.3433 5.75185C11.8588 5.57692 12.4167 5.57117 12.9357 5.73546C13.4547 5.89975 13.9077 6.22548 14.2287 6.66519C14.5496 7.10491 14.7218 7.63563 14.72 8.18001V9.78001Z"
        fill="black"
      />
    </svg>
  );
}

function IconBill() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_5216_103389)">
        <path
          d="M5.88578 8.5418H8.44578C8.53065 8.5418 8.61204 8.57551 8.67206 8.63552C8.73207 8.69553 8.76578 8.77693 8.76578 8.8618V9.5018C8.76578 9.58667 8.73207 9.66806 8.67206 9.72807C8.61204 9.78808 8.53065 9.8218 8.44578 9.8218H5.24578C5.16091 9.8218 5.07952 9.85551 5.01951 9.91552C4.9595 9.97553 4.92578 10.0569 4.92578 10.1418C4.92578 10.2267 4.9595 10.3081 5.01951 10.3681C5.07952 10.4281 5.16091 10.4618 5.24578 10.4618H6.84578L6.84578 10.7818C6.84578 10.8667 6.8795 10.9481 6.93951 11.0081C6.99952 11.0681 7.08091 11.1018 7.16578 11.1018C7.25065 11.1018 7.33204 11.0681 7.39206 11.0081C7.45207 10.9481 7.48578 10.8667 7.48578 10.7818V10.4618H8.44578C8.70039 10.4618 8.94457 10.3607 9.1246 10.1806C9.30464 10.0006 9.40578 9.7564 9.40578 9.5018V8.8618C9.40578 8.60719 9.30464 8.36301 9.1246 8.18297C8.94457 8.00294 8.70039 7.9018 8.44578 7.9018H5.88578C5.80091 7.9018 5.71952 7.86808 5.65951 7.80807C5.5995 7.74806 5.56578 7.66667 5.56578 7.5818V6.9418C5.56578 6.85693 5.5995 6.77553 5.65951 6.71552C5.71952 6.65551 5.80091 6.6218 5.88578 6.6218H9.08578C9.17065 6.6218 9.25204 6.58808 9.31206 6.52807C9.37207 6.46806 9.40578 6.38667 9.40578 6.3018C9.40578 6.21693 9.37207 6.13553 9.31206 6.07552C9.25204 6.01551 9.17065 5.9818 9.08578 5.9818H7.48578V5.6618C7.48578 5.57693 7.45207 5.49553 7.39206 5.43552C7.33204 5.37551 7.25065 5.3418 7.16578 5.3418C7.08091 5.3418 6.99952 5.37551 6.93951 5.43552C6.8795 5.49553 6.84578 5.57693 6.84578 5.6618V5.9818H5.88578C5.63117 5.9818 5.38699 6.08294 5.20696 6.26297C5.02692 6.44301 4.92578 6.68719 4.92578 6.9418V7.5818C4.92578 7.8364 5.02692 8.08058 5.20696 8.26062C5.38699 8.44065 5.63117 8.5418 5.88578 8.5418Z"
          fill="black"
        />
        <path
          d="M14.2059 13.6614H13.2459L13.2459 1.18138C13.2459 1.12349 13.2302 1.0667 13.2004 1.01705C13.1707 0.967401 13.128 0.926751 13.077 0.89943C13.0259 0.872109 12.9685 0.859141 12.9106 0.861907C12.8528 0.864673 12.7968 0.883069 12.7487 0.915136L11.0059 2.07738L9.26322 0.915136C9.21063 0.880052 9.14883 0.861328 9.08562 0.861328C9.0224 0.861328 8.9606 0.880052 8.90802 0.915136L7.16594 2.07738L5.42322 0.915136C5.37063 0.880052 5.30883 0.861328 5.24562 0.861328C5.1824 0.861328 5.1206 0.880052 5.06802 0.915136L3.32594 2.07738L1.58322 0.915136C1.53503 0.883069 1.47904 0.864673 1.42123 0.861907C1.36341 0.859141 1.30593 0.872109 1.2549 0.89943C1.20387 0.926751 1.1612 0.967401 1.13145 1.01705C1.1017 1.0667 1.08597 1.12349 1.08594 1.18138L1.08594 15.2614C1.08586 15.6302 1.21323 15.9878 1.44648 16.2735C1.67973 16.5592 2.00454 16.7556 2.36594 16.8294V16.8614H13.2459C13.5854 16.8614 13.911 16.7265 14.151 16.4865C14.3911 16.2464 14.5259 15.9209 14.5259 15.5814V13.9814C14.5259 13.8965 14.4922 13.8151 14.4322 13.7551C14.3722 13.6951 14.2908 13.6614 14.2059 13.6614ZM1.72594 15.2614L1.72594 1.77914L3.14866 2.72762C3.20124 2.7627 3.26304 2.78142 3.32626 2.78142C3.38947 2.78142 3.45127 2.7627 3.50386 2.72762L5.24594 1.56538L6.98866 2.72698C7.04124 2.76206 7.10304 2.78078 7.16626 2.78078C7.22947 2.78078 7.29127 2.76206 7.34386 2.72698L9.08594 1.56538L10.8287 2.72698C10.8812 2.76206 10.943 2.78078 11.0063 2.78078C11.0695 2.78078 11.1313 2.76206 11.1839 2.72698L12.6059 1.77914L12.6059 13.6614L3.96594 13.6614C3.88107 13.6614 3.79967 13.6951 3.73966 13.7551C3.67965 13.8151 3.64594 13.8965 3.64594 13.9814V15.2614C3.64594 15.516 3.5448 15.7602 3.36476 15.9402C3.18473 16.1202 2.94055 16.2214 2.68594 16.2214C2.43133 16.2214 2.18715 16.1202 2.00711 15.9402C1.82708 15.7602 1.72594 15.516 1.72594 15.2614ZM13.8859 15.5814C13.8859 15.7511 13.8185 15.9139 13.6985 16.0339C13.5785 16.1539 13.4157 16.2214 13.2459 16.2214H3.9589C4.17009 15.9458 4.28499 15.6086 4.28594 15.2614V14.3014L13.8859 14.3014V15.5814Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_5216_103389">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(0.0859375 0.861328)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function IconLockAlt() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      className="lock-alt-icon"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2137_21653)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M22.0642 11.2799H20.4322V6.3839C20.4322 4.79685 19.8017 3.27479 18.6795 2.15258C17.5573 1.03036 16.0353 0.399902 14.4482 0.399902C12.8611 0.399902 11.3391 1.03036 10.2169 2.15258C9.09465 3.27479 8.4642 4.79685 8.4642 6.3839V11.2799H6.8322C6.39936 11.2799 5.98426 11.4518 5.6782 11.7579C5.37214 12.064 5.2002 12.4791 5.2002 12.9119V25.9679C5.2002 26.4007 5.37214 26.8158 5.6782 27.1219C5.98426 27.428 6.39936 27.5999 6.8322 27.5999H22.0642C22.497 27.5999 22.9121 27.428 23.2182 27.1219C23.5243 26.8158 23.6962 26.4007 23.6962 25.9679V12.9119C23.6962 12.4791 23.5243 12.064 23.2182 11.7579C22.9121 11.4518 22.497 11.2799 22.0642 11.2799ZM19.3442 6.3839V11.2799H9.5522V6.3839C9.5522 5.0854 10.068 3.84009 10.9862 2.92191C11.9044 2.00373 13.1497 1.4879 14.4482 1.4879C15.7467 1.4879 16.992 2.00373 17.9102 2.92191C18.8284 3.84009 19.3442 5.0854 19.3442 6.3839ZM16.2977 18.55C16.2977 19.3609 15.7759 20.05 15.0497 20.2997L16.4549 22.6574C16.5224 22.7707 16.4408 22.9144 16.3088 22.9144H12.588C12.4561 22.9144 12.3744 22.7707 12.442 22.6574L13.8469 20.3001C13.1202 20.0506 12.598 19.3613 12.598 18.55C12.598 17.5284 13.4262 16.7001 14.4478 16.7001C15.4695 16.7001 16.2977 17.5284 16.2977 18.55Z"
          fill="#FAFAFA"
        />
      </g>
      <defs>
        <clipPath id="clip0_2137_21653">
          <rect
            width="27.2"
            height="27.2"
            fill="white"
            transform="translate(0.100098 0.399902)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

function Dot(props: Partial<IconProps>) {
  const { color, size } = props;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="8" fill={color ? color : "#3F3276"} />
    </svg>
  );
}

function State(props: Partial<IconProps>) {
  const { color, size = 20 } = props;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill={color}
      className="bi bi-pin-map"
      viewBox="0 0 16 16"
    >
      <path
        fillRule="evenodd"
        d="M3.1 11.2a.5.5 0 0 1 .4-.2H6a.5.5 0 0 1 0 1H3.75L1.5 15h13l-2.25-3H10a.5.5 0 0 1 0-1h2.5a.5.5 0 0 1 .4.2l3 4a.5.5 0 0 1-.4.8H.5a.5.5 0 0 1-.4-.8z"
      />
      <path
        fillRule="evenodd"
        d="M8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6M4 4a4 4 0 1 1 4.5 3.969V13.5a.5.5 0 0 1-1 0V7.97A4 4 0 0 1 4 3.999z"
      />
    </svg>
  );
}

function CheckFullCircle(props: Partial<IconProps>) {
  const { color, size } = props;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 25 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1204_11459)">
        <path
          d="M24.3789 12.0505C24.3789 15.2331 23.1146 18.2854 20.8642 20.5358C18.6138 22.7863 15.5615 24.0505 12.3789 24.0505C9.19631 24.0505 6.14406 22.7863 3.89363 20.5358C1.64319 18.2854 0.378906 15.2331 0.378906 12.0505C0.378906 8.86794 1.64319 5.81569 3.89363 3.56526C6.14406 1.31482 9.19631 0.0505371 12.3789 0.0505371C15.5615 0.0505371 18.6138 1.31482 20.8642 3.56526C23.1146 5.81569 24.3789 8.86794 24.3789 12.0505ZM18.4239 7.50554C18.3168 7.39877 18.1892 7.3147 18.0488 7.25836C17.9084 7.20202 17.7581 7.17457 17.6069 7.17766C17.4557 7.18074 17.3066 7.21428 17.1686 7.27629C17.0307 7.3383 16.9066 7.42749 16.8039 7.53854L11.5944 14.176L8.45491 11.035C8.24164 10.8363 7.95957 10.7281 7.66812 10.7333C7.37667 10.7384 7.09859 10.8565 6.89247 11.0626C6.68635 11.2687 6.56829 11.5468 6.56314 11.8383C6.558 12.1297 6.66619 12.4118 6.86491 12.625L10.8339 16.5955C10.9408 16.7023 11.0682 16.7864 11.2083 16.8428C11.3484 16.8993 11.4985 16.9269 11.6495 16.9241C11.8006 16.9213 11.9495 16.8881 12.0875 16.8265C12.2254 16.7649 12.3495 16.6762 12.4524 16.5655L18.4404 9.08054C18.6445 8.86829 18.7573 8.58445 18.7545 8.28997C18.7517 7.9955 18.6336 7.71386 18.4254 7.50554H18.4239Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

function LocationPinIcon(props: Partial<IconProps>) {
  const { size, className } = props;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      version="1.1"
    >
      <g
        id="Location-Pin"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <path
          d="M32,10 C41.3894244,10 49,17.5401971 49,26.8427933 C49,35.8524406 37.8281643,47.6551021 32,55 C26.1706482,47.6539256 15,35.8524406 15,26.8427933 C15,17.5401971 22.6105756,10 32,10 Z M31.6703171,18.681874 C35.9064395,18.681874 39.3396621,22.0843112 39.3396621,26.2812602 C39.3396621,30.4782093 35.9064395,33.8796833 31.6703171,33.8796833 C27.4341947,33.8796833 24,30.4782093 24,26.2812602 C24,22.0843112 27.4341947,18.681874 31.6703171,18.681874 Z"
          id="Shape"
          stroke="#F8F8F1"
          fill="#3400FF"
        ></path>
      </g>
    </svg>
  );
}

function ChevronDownIcon(props: Partial<IconProps>) {
  const { className } = props;
  return (
    <svg
      className={className}
      width="1em"
      height="1em"
      viewBox="0 0 30 30"
      version="1.1"
    >
      <g
        id="Arrow"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <path
          d="M15.7071068,12.7071068 L19.2928932,16.2928932 C19.6834175,16.6834175 19.6834175,17.3165825 19.2928932,17.7071068 C19.1053568,17.8946432 18.8510029,18 18.5857864,18 L11.4142136,18 C10.8619288,18 10.4142136,17.5522847 10.4142136,17 C10.4142136,16.7347835 10.5195704,16.4804296 10.7071068,16.2928932 L14.2928932,12.7071068 C14.6834175,12.3165825 15.3165825,12.3165825 15.7071068,12.7071068 Z"
          id="Triangle-Copy-10"
          fill="#D7D5DD"
          transform="translate(15.000000, 15.000000) scale(1, -1) translate(-15.000000, -15.000000) "
        ></path>
      </g>
    </svg>
  );
}

function ChevronUpIcon(props: Partial<IconProps>) {
  const { className } = props;
  return (
    <svg
      className={className}
      width="1em"
      height="1em"
      viewBox="0 0 30 30"
      version="1.1"
    >
      <g
        id="Arrow"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <path
          d="M15.7071068,12.7071068 L19.2928932,16.2928932 C19.6834175,16.6834175 19.6834175,17.3165825 19.2928932,17.7071068 C19.1053568,17.8946432 18.8510029,18 18.5857864,18 L11.4142136,18 C10.8619288,18 10.4142136,17.5522847 10.4142136,17 C10.4142136,16.7347835 10.5195704,16.4804296 10.7071068,16.2928932 L14.2928932,12.7071068 C14.6834175,12.3165825 15.3165825,12.3165825 15.7071068,12.7071068 Z"
          id="Triangle-Copy-10"
          fill="#D7D5DD"
          transform="translate(15.000000, 15.000000) scale(1, -1) rotate(180) translate(-15.000000, -15.000000)"
        ></path>
      </g>
    </svg>
  );
}

function LayoutIcon(props: Partial<IconProps>) {
  const { size = 30, className } = props;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 30 30"
      version="1.1"
    >
      <g
        id="Layout"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="Budicon-Outline/Layout/card-stack-view"
          transform="translate(4.000000, 4.000000)"
          fill="#000000"
          fillRule="nonzero"
        >
          <path
            d="M19.8,1.76 L3.08,1.76 C2.35098413,1.76 1.76,2.35098413 1.76,3.08 L1.76,18.92 C1.76,19.6490159 2.35098413,20.24 3.08,20.24 L19.8,20.24 C20.5290159,20.24 21.12,19.6490159 21.12,18.92 L21.12,3.08 C21.12,2.35098413 20.5290159,1.76 19.8,1.76 Z M20.24,18.92 C20.24,19.1630053 20.0430053,19.36 19.8,19.36 L3.08,19.36 C2.83699471,19.36 2.64,19.1630053 2.64,18.92 L2.64,3.08 C2.64,2.83699471 2.83699471,2.64 3.08,2.64 L19.8,2.64 C20.0430053,2.64 20.24,2.83699471 20.24,3.08 L20.24,18.92 Z M5.28,7.92 L17.6,7.92 L17.6,4.4 L5.28,4.4 L5.28,7.92 Z M6.16,5.28 L16.72,5.28 L16.72,7.04 L6.16,7.04 L6.16,5.28 Z M6.16,17.6 L10.56,17.6 L10.56,9.68 L6.16,9.68 L6.16,17.6 Z M7.04,10.56 L9.68,10.56 L9.68,16.72 L7.04,16.72 L7.04,10.56 Z M12.32,15.84 L16.72,15.84 L16.72,9.68 L12.32,9.68 L12.32,15.84 Z M13.2,10.56 L15.84,10.56 L15.84,14.96 L13.2,14.96 L13.2,10.56 Z"
            id="Shape"
          ></path>
        </g>
      </g>
    </svg>
  );
}

function ShareIcon(props: Partial<IconProps>) {
  const { size = 30, color = "#BFBFBF", className } = props;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 30 30"
      version="1.1"
    >
      <g
        id="Share"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="Budicon-Outline/Interface/share"
          transform="translate(5.000000, 3.000000)"
          fill={color}
          fillRule="nonzero"
          stroke={color}
        >
          <path
            d="M5.1465,5.85339323 C4.95130903,5.6581433 4.95130903,5.34164315 5.1465,5.14639323 L10.1465,0.146393227 C10.3417499,-0.0487977423 10.6582501,-0.0487977423 10.8535,0.146393227 L15.8535,5.14639323 C15.9834783,5.27193046 16.0356063,5.45783217 15.9898486,5.63264689 C15.9440909,5.8074616 15.8075684,5.94398408 15.6327537,5.98974181 C15.4579389,6.03549953 15.2720372,5.98337152 15.1465,5.85339323 L11,1.70689323 L11,20.4998932 C11,20.7760356 10.7761424,20.9998932 10.5,20.9998932 C10.2238576,20.9998932 10,20.7760356 10,20.4998932 L10,1.70689323 L5.8535,5.85339323 C5.65825007,6.0485842 5.34174993,6.0485842 5.1465,5.85339323 Z M19.5,7.99989323 L16.5,7.99989323 C16.2238576,7.99989323 16,8.22375085 16,8.49989323 C16,8.7760356 16.2238576,8.99989323 16.5,8.99989323 L19.5,8.99989323 C19.7761424,8.99989323 20,9.22375085 20,9.49989323 L20,23.4998932 C20,23.7760356 19.7761424,23.9998932 19.5,23.9998932 L1.5,23.9998932 C1.22385763,23.9998932 1,23.7760356 1,23.4998932 L1,9.49989323 C1,9.22375085 1.22385763,8.99989323 1.5,8.99989323 L4.5,8.99989323 C4.77614237,8.99989323 5,8.7760356 5,8.49989323 C5,8.22375085 4.77614237,7.99989323 4.5,7.99989323 L1.5,7.99989323 C0.671572875,7.99989323 0,8.6714661 0,9.49989323 L0,23.4998932 C0,24.3283204 0.671572875,24.9998932 1.5,24.9998932 L19.5,24.9998932 C20.3284271,24.9998932 21,24.3283204 21,23.4998932 L21,9.49989323 C21,8.6714661 20.3284271,7.99989323 19.5,7.99989323 Z"
            id="Shape"
          ></path>
        </g>
      </g>
    </svg>
  );
}

function Pencil(props: Partial<IconProps>) {
  const { className, color } = props;
  return (
    <svg className={className} width="1em" height="1em" viewBox="0 0 10 10">
      <path
        d="M0 7.91696V10H2.08304L8.22663 3.85641L6.14359 1.77336L0 7.91696ZM9.83752 2.24552C10.0542 2.02888 10.0542 1.67893 9.83752 1.4623L8.5377 0.162477C8.32107 -0.0541591 7.97111 -0.0541591 7.75448 0.162477L6.73795 1.179L8.821 3.26205L9.83752 2.24552Z"
        fill={color}
      />
    </svg>
  );
}
